import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  FileText,
  MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CATEGORIES = ["Cabinetry", "Remodel", "Refacing", "Refinishing"];
const SUB_CATEGORIES = {
  Cabinetry: ["Kitchen", "Vanity", "Built-in", "Bar", "Closet", "Other"],
  Remodel: ["Kitchen", "Bathroom", "Mudroom", "Other"],
  Refacing: [],
  Refinishing: []
};

export default function EstimateTemplatesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [form, setForm] = useState({
    name: "",
    category: "",
    sub_category: "",
    description: "",
    default_terms: "50% deposit required to begin work. Balance due upon completion. This estimate is valid for 30 days.",
    default_tax_rate: 13,
    default_deposit_percentage: 50,
    door_styles: [],
    door_colors: [],
    default_line_items: []
  });

  useEffect(() => {
    fetchTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/estimate-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/estimate-templates/${editingTemplate.template_id}`, form);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/estimate-templates`, form);
        toast.success("Template created");
      }
      fetchTemplates();
      closeModal();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/estimate-templates/${templateId}`);
      setTemplates(templates.filter(t => t.template_id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      category: template.category,
      sub_category: template.sub_category || "",
      description: template.description || "",
      default_terms: template.default_terms || "",
      default_tax_rate: template.default_tax_rate || 13,
      default_deposit_percentage: template.default_deposit_percentage || 50,
      door_styles: template.door_styles || [],
      door_colors: template.door_colors || [],
      default_line_items: template.default_line_items || []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setForm({
      name: "",
      category: "",
      sub_category: "",
      description: "",
      default_terms: "50% deposit required to begin work. Balance due upon completion. This estimate is valid for 30 days.",
      default_tax_rate: 13,
      default_deposit_percentage: 50,
      door_styles: [],
      door_colors: [],
      default_line_items: []
    });
  };

  const addDefaultLineItem = () => {
    setForm({
      ...form,
      default_line_items: [
        ...form.default_line_items,
        { description: "", item_type: "door", unit_price: 0 }
      ]
    });
  };

  const updateDefaultLineItem = (index, field, value) => {
    const updated = [...form.default_line_items];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, default_line_items: updated });
  };

  const removeDefaultLineItem = (index) => {
    setForm({
      ...form,
      default_line_items: form.default_line_items.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimate-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Estimate Templates</h1>
            <p className="text-slate-600">{templates.length} templates</p>
          </div>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(template => (
          <Card key={template.template_id} className="hover:shadow-md transition-shadow">
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{template.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">{template.category}</Badge>
                    {template.sub_category && (
                      <Badge variant="outline">{template.sub_category}</Badge>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-slate-500 mt-2">{template.description}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    {template.default_line_items?.length || 0} default items
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(template)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(template.template_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No templates yet</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowModal(true)}>
                Create First Template
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Template Modal */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "New Template"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Template Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Kitchen Refacing Standard"
                  required
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select 
                  value={form.category} 
                  onValueChange={(v) => setForm({ ...form, category: v, sub_category: "" })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {form.category && SUB_CATEGORIES[form.category]?.length > 0 && (
                <div>
                  <Label>Sub-Category</Label>
                  <Select 
                    value={form.sub_category} 
                    onValueChange={(v) => setForm({ ...form, sub_category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sub-category" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUB_CATEGORIES[form.category].map(sub => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What is this template for?"
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Default Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={form.default_tax_rate}
                  onChange={(e) => setForm({ ...form, default_tax_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Default Deposit (%)</Label>
                <Input
                  type="number"
                  value={form.default_deposit_percentage}
                  onChange={(e) => setForm({ ...form, default_deposit_percentage: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label>Default Terms & Conditions</Label>
              <Textarea
                value={form.default_terms}
                onChange={(e) => setForm({ ...form, default_terms: e.target.value })}
                rows={3}
              />
            </div>

            {/* Default Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Default Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDefaultLineItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              {form.default_line_items.length > 0 ? (
                <div className="space-y-2">
                  {form.default_line_items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <Input
                        value={item.description}
                        onChange={(e) => updateDefaultLineItem(index, "description", e.target.value)}
                        placeholder="Description"
                        className="flex-1"
                      />
                      <Select 
                        value={item.item_type} 
                        onValueChange={(v) => updateDefaultLineItem(index, "item_type", v)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="door">Door</SelectItem>
                          <SelectItem value="drawer">Drawer</SelectItem>
                          <SelectItem value="panel">Panel</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateDefaultLineItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                        placeholder="Price"
                        className="w-24"
                      />
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="icon" 
                        onClick={() => removeDefaultLineItem(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No default line items
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              <Button type="submit" disabled={!form.name || !form.category}>
                {editingTemplate ? "Update Template" : "Create Template"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
